import { useCallback } from 'react';

import { MedicalDeviceOrder } from '@dreryk/egabinet-ezwm-frontend-api';
import { Disease } from '@dreryk/medical-dictionaries-api';

import { createTranslatedText, PromiseVoidFunction } from '@dreryk/react-common';
import { NavTabs } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';

import { VisitDocumentContextProvider } from '../../common/visitDocument/VisitDocumentContext';

import { MedicalDeviceOrderForm } from './medicalDeviceOrder/MedicalDeviceOrderForm';
import { MedicalDeviceOrderFormLoader } from './medicalDeviceOrder/MedicalDeviceOrderForm.loader';
import { mapMedicalDeviceDraftToFormValues } from './medicalDeviceOrder/medicalDeviceOrder.utils';
import { MedicalDeviceOrdersHistory } from './medicalDeviceOrdersHistory/MedicalDeviceOrdersHistory';
import { medicalDevicesTranslationKeys } from './translations';

const ADULT_AGE = 18;

type MedicalDevicesPanelTabsProps = {
   readonly visitDisease?: Disease;
   readonly visitId: number;
   readonly patientId: number;
   readonly patientAge: number;
   readonly existingOrder: MedicalDeviceOrder | undefined;
   readonly refetchOrder: PromiseVoidFunction;
};

export const MedicalDevicesPanelTabs = (props: MedicalDevicesPanelTabsProps): JSX.Element => {
   const { refetchOrder } = props;
   const { documentNumber } = props.existingOrder ?? ({} as MedicalDeviceOrder);

   const handleTabChange = useCallback(
      (tabParam: visitPaths.MedicalDevicesPanelNavParams) => {
         if (visitPaths.MedicalDevicesPanelNavParams.NEW_ORDER === tabParam) {
            refetchOrder();
         }
      },
      [refetchOrder]
   );

   return (
      <NavTabs<visitPaths.MedicalDevicesPanelNavParams> testId={'medicalDevices'} onTabChange={handleTabChange}>
         <NavTabs.Tab
            testId={visitPaths.MedicalDevicesPanelNavParams.NEW_ORDER}
            param={visitPaths.MedicalDevicesPanelNavParams.NEW_ORDER}
            label={documentNumber ? createTranslatedText(documentNumber) : medicalDevicesTranslationKeys.tabs.newOrder}
         >
            <MedicalDeviceOrderFormLoader patientId={props.patientId}>
               {({ patient }) => {
                  const defaultValues = props.existingOrder
                     ? mapMedicalDeviceDraftToFormValues(props.existingOrder)
                     : undefined;

                  return (
                     <VisitDocumentContextProvider visitId={props.visitId} externalId={props.existingOrder?.externalId}>
                        <MedicalDeviceOrderForm
                           shouldDisplayPregnantField={patient?.gender === 'WOMAN'}
                           visitDisease={props.visitDisease}
                           visitId={props.visitId}
                           draftValues={defaultValues}
                           documentVerificationErrors={props.existingOrder?.validationErrors ?? []}
                           printable={props.existingOrder?.printableWithoutApproval}
                           existingOrderId={props.existingOrder?.id}
                           onDocumentVerificationErrors={props.refetchOrder}
                           forChildren={ADULT_AGE > props.patientAge}
                        />
                     </VisitDocumentContextProvider>
                  );
               }}
            </MedicalDeviceOrderFormLoader>
         </NavTabs.Tab>
         <NavTabs.Tab
            testId={visitPaths.MedicalDevicesPanelNavParams.ORDERS_HISTORY}
            param={visitPaths.MedicalDevicesPanelNavParams.ORDERS_HISTORY}
            label={medicalDevicesTranslationKeys.tabs.ordersHistory}
         >
            <MedicalDeviceOrdersHistory patientId={props.patientId} visitId={props.visitId} />
         </NavTabs.Tab>
      </NavTabs>
   );
};
